import React, { useEffect, useState } from 'react';
import { ROLES, SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import { useHistory, useLocation } from 'react-router-dom';
import { SquadPlayer } from './types';
import Button from '@mui/material/Button';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import EditPlayerModal from './components/player/edit-player-modal';
import Box from '@mui/material/Box';
import PlayerHeader from './components/player/player-header';
import RemovePlayerModal from './components/player/remove-player-modal';
import PlayerTabInfo from './components/player/player-tab-info';
import PlayerTabSocial from './components/player/player-tab-social';
import PlayerTabStats from './components/player/player-tab-stats';
import PlayerTabData from './components/player/player-tab-data';
import PlayerTabContract from './components/player/player-tab-contract';
import { usePlayer } from './hooks/player';
import PlayerTabHealth from './components/player/player-tab-health';
import { useUserAuth } from '../../../contexts/auth';
import { useSquad } from './hooks/squad';
import { isArray } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import ScenePage from '../../common/scene/scene-page';
import { Typography } from '@mui/material';
import useTeamData from '../../../hooks/useTeamData';

export enum PLAYER_TABS {
  INFORMATION = 'information',
  SOCIAL = 'social',
  HEALTH = 'health',
  CONTRACT = 'contract',
  STATS = 'stats',
  DATA = 'data',
}

export interface PlayerState {
  player: SquadPlayer;
  teamId: string;
  selectedSquad: SquadPlayer[];
  selectedSeason: number;
  selectedTab?: PLAYER_TABS;
  backPage?: string;
}

const styles: SxStyle = {
  container: {
    paddingTop: '32px',
    paddingBottom: '16px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  tabs: {
    marginTop: '10px',
  },
};

const PlayerContainer = () => {
  const { strings } = useStringsContext();
  const location = useLocation();
  const history = useHistory();
  const { fetchPlayer } = usePlayer();
  const [selectedTab, setSelectedTab] = useState(PLAYER_TABS.INFORMATION);
  const [showEditPlayer, setShowEditPlayer] = useState(false);
  const [showRemovePlayer, setShowRemovePlayer] = useState(false);
  const [player, setPlayer] = useState<SquadPlayer | null>(null);
  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const {
    player: squadPlayer,
    teamId,
    selectedSquad,
    selectedSeason,
    selectedTab: initialTab,
    backPage,
  } = location.state as PlayerState;
  const breadCrumps = useBreadcrumbs(
    player
      ? {
          afterLinks: [
            {
              to: '',
              text: player.displayName || player.fullName,
            },
          ],
          beforeLinks: [],
        }
      : undefined
  );

  const tabs = [
    { id: PLAYER_TABS.INFORMATION, label: strings.squadPlayerTabs.information },
    { id: PLAYER_TABS.SOCIAL, label: strings.squadPlayerTabs.social },
    { id: PLAYER_TABS.HEALTH, label: strings.squadPlayerTabs.health },
    { id: PLAYER_TABS.CONTRACT, label: strings.squadPlayerTabs.contract },
  ];

  const team = useTeamData({ teamAbbrv: teamId });

  const isReadOnly = !isSuperAdmin && hasTeamRole(teamId, ROLES.VIEWER);

  const hideStats = true;
  if (!hideStats) {
    tabs.push({ id: PLAYER_TABS.STATS, label: strings.squadPlayerTabs.stats });
  }
  if (isSuperAdmin) {
    tabs.push({ id: PLAYER_TABS.DATA, label: strings.squadPlayerTabs.data });
  }

  const { fetchSquad } = useSquad();
  const selectedSquadReq = useQuery({
    queryKey: ['squad', player?.activeCurrentClub?.id, selectedSeason],
    queryFn: async (): Promise<SquadPlayer[]> => {
      if (selectedSquad) {
        return selectedSquad;
      }

      if (!player?.activeCurrentClub?.id) {
        return [];
      }
      const squad = await fetchSquad(player.activeCurrentClub.id, selectedSeason);
      if (squad && isArray(squad)) {
        return squad;
      }
      return [];
    },
  });

  useEffect(() => {
    if (squadPlayer) {
      setPlayer(squadPlayer);
    }
  }, [squadPlayer]);

  useEffect(() => {
    if (initialTab) {
      setSelectedTab(initialTab);
    }
  }, [initialTab]);

  const onUpdatePlayer = async (updatedPlayer: SquadPlayer) => {
    const newPlayer = await fetchPlayer(teamId, updatedPlayer.fogisId);
    if (newPlayer) {
      setPlayer(newPlayer);
    }
  };

  const onSelectTab = (newTab: PLAYER_TABS) => {
    setSelectedTab(newTab);
  };

  const onEditPlayer = () => {
    setShowEditPlayer(true);
  };

  const onDeletePlayer = () => {
    setShowRemovePlayer(true);
  };

  const onRemovedPlayer = (removedPlayer: SquadPlayer) => {
    if (backPage) {
      history.push(backPage);
    } else {
      history.replace(`/squad/${teamId}${selectedSeason ? `?year=${selectedSeason}` : ''}`);
    }
  };

  const onSelectedPlayer = (selectedPlayer: SquadPlayer) => {
    history.replace(`/squad/${teamId}/player`, {
      player: selectedPlayer,
      teamId,
      selectedSquad,
      selectedSeason,
    } as PlayerState);
  };

  // TODO improve
  if (player === null) {
    return <></>;
  }

  return (
    <ScenePage title={strings.appBar.squad} breadCrumps={breadCrumps}>
      <Typography variant="h5" sx={{ ml: 1 }}>
        {team.query.data?.name} - {selectedSeason}
      </Typography>

      {backPage ? (
        <Button
          onClick={() => {
            history.push(backPage);
          }}
          variant="outlined"
          sx={{ ml: 1 }}
          startIcon={<ArrowBackIosIcon />}
        >
          {strings.allPlayers.backButton}
        </Button>
      ) : null}
      <Box sx={styles.container}>
        <Paper elevation={2}>
          <PlayerHeader
            selectedSquad={selectedSquadReq.data || []}
            isLoading={selectedSquadReq.isLoading}
            player={player}
            onDeletePlayer={onDeletePlayer}
            onSelectedPlayer={onSelectedPlayer}
            isReadOnly={isReadOnly}
          />
          <Tabs
            sx={styles.tabs}
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={(event, newTab) => {
              onSelectTab(newTab);
            }}
          >
            {tabs.map((tab) => (
              <Tab label={tab.label} value={tab.id} key={tab.id} />
            ))}
          </Tabs>
          {player && selectedTab === PLAYER_TABS.INFORMATION && (
            <PlayerTabInfo
              teamId={teamId}
              player={player}
              onUpdatePlayer={onUpdatePlayer}
              onEditPlayer={onEditPlayer}
              season={selectedSeason}
              isReadOnly={isReadOnly}
            />
          )}
          {player && selectedTab === PLAYER_TABS.SOCIAL && (
            <PlayerTabSocial
              player={player}
              onEditPlayer={onEditPlayer}
              season={selectedSeason}
              isReadOnly={isReadOnly}
            />
          )}
          {player && selectedTab === PLAYER_TABS.HEALTH && (
            <PlayerTabHealth
              player={player}
              onEditPlayer={onEditPlayer}
              season={selectedSeason}
              isReadOnly={isReadOnly}
            />
          )}
          {player && selectedTab === PLAYER_TABS.CONTRACT && (
            <PlayerTabContract
              teamId={teamId}
              player={player}
              onEditPlayer={onEditPlayer}
              season={selectedSeason}
              isReadOnly={isReadOnly}
            />
          )}
          {!hideStats && player && selectedTab === PLAYER_TABS.STATS ? (
            <PlayerTabStats player={player} season={selectedSeason} />
          ) : null}
          {isSuperAdmin && player && selectedTab === PLAYER_TABS.DATA && (
            <PlayerTabData player={player} onEditPlayer={onEditPlayer} season={selectedSeason} />
          )}
        </Paper>
      </Box>
      {showEditPlayer && (
        <EditPlayerModal
          selectedTab={selectedTab}
          open={showEditPlayer}
          onClose={() => setShowEditPlayer(false)}
          onUpdatePlayer={onUpdatePlayer}
          player={player}
          teamId={teamId}
        />
      )}
      {showRemovePlayer && (
        <RemovePlayerModal
          open={showRemovePlayer}
          onClose={() => setShowRemovePlayer(false)}
          onRemovedPlayer={onRemovedPlayer}
          player={player}
          season={selectedSeason}
          teamId={teamId}
        />
      )}
    </ScenePage>
  );
};

export default PlayerContainer;
